import React from 'react';
import { View, Text, Image } from '@react-pdf/renderer';
import { pdfStyles } from '../PDFStyles';
import { preprocessTextForPDF } from '../EmojiProcessor';

interface ContentSectionProps {
  formData: {
    content: string;
    platform: string;
    sender: string;
    sector: string;
  };
  simulation: any;
  forecast: any;
}

// Helper function to get initials from name
const getInitials = (name: string) => {
  if (!name) return '?';
  return name.split(' ')
    .map(word => word[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();
};

// Helper function to format numbers for display
const formatNumber = (num?: number) => {
  if (num === undefined) return '0';
  if (num < 1000) return num.toString();
  if (num < 1000000) return `${(num / 1000).toFixed(1)}K`;
  return `${(num / 1000000).toFixed(1)}M`;
};

// Helper function to get platform-specific engagement stats
const getPlatformEngagementStats = (platform: string, forecast: any) => {
  const platformLower = platform?.toLowerCase();
  
  // Default values with safe fallbacks
  const replies7d = forecast?.replies7d || 0;
  const likes7d = forecast?.likes7d || 0;
  
  switch (platformLower) {
    case 'tiktok':
      return [
        { number: formatNumber(replies7d), label: 'Comments' },
        { number: formatNumber(Math.round(likes7d * 0.2)), label: 'Shares' },
        { number: formatNumber(likes7d), label: 'Hearts' }
      ];
    case 'facebook':
      return [
        { number: formatNumber(replies7d), label: 'Comments' },
        { number: formatNumber(Math.round(likes7d * 0.3)), label: 'Shares' },
        { number: formatNumber(likes7d), label: 'Likes' }
      ];
    case 'instagram':
      return [
        { number: formatNumber(replies7d), label: 'Comments' },
        { number: formatNumber(Math.round(likes7d * 0.15)), label: 'Shares' },
        { number: formatNumber(likes7d), label: 'Likes' }
      ];
    case 'linkedin':
      return [
        { number: formatNumber(replies7d), label: 'Comments' },
        { number: formatNumber(Math.round(likes7d * 0.2)), label: 'Shares' },
        { number: formatNumber(likes7d), label: 'Likes' }
      ];
    case 'reddit':
      return [
        { number: formatNumber(replies7d), label: 'Comments' },
        { number: formatNumber(Math.round(likes7d * 0.1)), label: 'Shares' },
        { number: formatNumber(likes7d), label: 'Upvotes' }
      ];
    case 'youtube':
      return [
        { number: formatNumber(replies7d), label: 'Comments' },
        { number: formatNumber(Math.round(likes7d * 0.05)), label: 'Shares' },
        { number: formatNumber(likes7d), label: 'Likes' }
      ];
    case 'twitter':
    case 'x':
    case 'twitter/x':
    default:
      return [
        { number: formatNumber(replies7d), label: 'Replies' },
        { number: formatNumber(Math.round(likes7d * 0.25)), label: 'Retweets' },
        { number: formatNumber(likes7d), label: 'Likes' }
      ];
  }
};

export const ContentSection: React.FC<ContentSectionProps> = ({ 
  formData, 
  simulation, 
  forecast 
}) => {
  return (
    <View style={pdfStyles.contentPreview}>
      <Text style={pdfStyles.cardTitle}>Content Preview</Text>
      
      {/* Header with avatar, name, handle, verified badge, timestamp */}
      <View style={pdfStyles.postHeader}>
        <View style={pdfStyles.avatar}>
          <Text style={pdfStyles.avatarText}>{getInitials(formData.sender)}</Text>
        </View>
        <View style={pdfStyles.userInfo}>
          <View style={pdfStyles.userInfoTop}>
            <Text style={pdfStyles.userName}>{formData.sender || 'Anonymous'}</Text>
            <Text style={pdfStyles.verifiedBadge}>✓</Text>
            <Text style={pdfStyles.userHandle}>@{formData.sender?.replace(/\s+/g, '').toLowerCase() || 'anonymous'}</Text>
            <Text style={pdfStyles.timestamp}>·</Text>
            <Text style={pdfStyles.timestamp}>1h</Text>
          </View>
        </View>
        <Text style={pdfStyles.moreOptions}>...</Text>
      </View>

      {/* Post Content */}
      <Text style={pdfStyles.postContent}>{preprocessTextForPDF(formData.content)}</Text>

      {/* Image if available */}
      {simulation?.imageUrl && (
        <Image 
          src={simulation.imageUrl} 
          style={pdfStyles.postImage}
        />
      )}

      {/* Engagement Stats - Platform Specific */}
      <View style={pdfStyles.postStats}>
        {getPlatformEngagementStats(formData.platform, forecast).map((stat, index) => (
          <View key={index} style={pdfStyles.statItem}>
            <Text style={pdfStyles.statNumber}>{stat.number}</Text>
            <Text style={pdfStyles.statLabel}> {stat.label}</Text>
          </View>
        ))}
      </View>
    </View>
  );
};